import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { ComplianceTable } from "~/components/Completion/ComplianceTable";
import { Loading, LoadingFailed } from "~/components/loading";
import {
  ChecksOrderField,
  CheckState,
  ExceptionMutationAction,
  ExceptionStatus,
  ExceptionType,
  GetComplianceControlChecksDocument,
  GetComplianceControlChecksQuery,
  GetComplianceControlDocument,
  Maybe,
  OrderDirection,
  PolicyOrPackRef,
  useGetComplianceControlChecksQuery,
} from "~/operations";
import { useSearch } from "~/components/search/useSearch";
import { EmptyState } from "~/components/empty-state/empty-state";
import { INITIAL_PAGE_RANGE, Pagination } from "~/components/pagination";
import { CompletionCell } from "../../../components/CompletionCell";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { Chip } from "~/components/chip";
import { NavigateToExceptionButton } from "~/components/exceptions/navigate-to-exception-button";
import { mapSelectedEntitiesToString } from "~/components/exceptions/utils/mapSelectedEntitiesToString";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { useControlContext } from "~/pages/compliance/Control/ComplianceControl";
import { ComplianceControlChecksSkeleton } from "./ComplianceControlChecksSkeleton";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import { useDialogs } from "@toolpad/core";
import {
  SelectedEntityWithFindingData,
  SetExceptionDialog,
} from "~/components/exceptions/SetExceptionDialog";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import { setDocumentTitle } from "~/lib/commonUtils";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { useExceptionsButtons } from "~/components/exceptions/use-exceptions-buttons";
import { useSelection } from "~/hooks/useSelection";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { Header } from "~/types/table";
import { DisabledIcon, FalsePositiveIcon, WorkaroundIcon } from "~/icons";

type ChecksConnection = NonNullable<
  GetComplianceControlChecksQuery["complianceControl"]["checks"]
>;
type ChecksEdge = NonNullable<ChecksConnection["edges"]>[0];
type ChecksNode = NonNullable<ChecksEdge["node"]>;
type QueryCheck = Extract<ChecksNode, { __typename: "QueryCheck" }>;

export type ComplianceControlChecksProps = {};

export function ComplianceControlChecks({}: ComplianceControlChecksProps) {
  const { space, controlMrn, frameworkMrn, frameworkName, control } =
    useControlContext();
  const [searchParams] = useSearchParams();
  const { searchFilters } = useSearch();
  const [pageItems, setPageItems] = useState(INITIAL_PAGE_RANGE);
  const { frameworkId, controlId } = useParams();
  const dialogs = useDialogs();
  const triageToolbar = useTriageToolbar();

  const hasApplyExceptionPermission = useCanApplyExceptionMutation();

  const theme = useTheme();
  const navigate = useNavigate();

  const {
    isGroupChecked,
    isGroupIndeterminate,
    onGroupCheckChange,
    selectedEntities: selectedChecks,
    setSelectedEntities: setSelectedChecks,
    handleNodeClick,
    handleNodeChange,
    handleCancelClick,
  } = useSelection<
    SelectedEntityWithException & SelectedEntityWithFindingData
  >();

  const {
    exceptionsButtonsAvailability,
    exceptionsButtonsVisibility,
    exceptionsButtonsTooltipText,
  } = useExceptionsButtons({
    selectedEntities: selectedChecks,
    target: "check",
    showExceptionButtons: true,
    showScopeButtons: false,
  });

  useEffect(() => {
    if (selectedChecks.length > 0) {
      triageToolbar.show({
        buttons: [
          ...(exceptionsButtonsVisibility.SET_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.CreateException,
                  onClick: handleSetExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.SET_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.SET_EXCEPTION,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.REMOVE_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.RemoveException,
                  onClick: handleRemoveExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.REMOVE_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.REMOVE_EXCEPTION,
                },
              ]
            : []),
        ],
        payload: {
          count: selectedChecks.length,
          totalCount: checksArray.length,
          onClose: handleCancelClick,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedChecks]);

  const { data, error, loading, fetchMore } =
    useGetComplianceControlChecksQuery({
      variables: {
        first: 50,
        input: {
          controlMrn,
          frameworkMrn,
          scopeMrn: space.mrn,
        },
        checksInput: {
          frameworkMrn,
          scopeMrn: space.mrn,
          orderBy: {
            field:
              (searchParams.get("field") as ChecksOrderField) ||
              ChecksOrderField.AssetsCount,
            direction:
              (searchParams.get("direction") as OrderDirection) ||
              OrderDirection.Desc,
          },
          query: searchFilters.join(","),
        },
      },
      initialFetchPolicy: "cache-and-network",
    });

  const { exceptionGroups } = useFetchExceptions({
    mrn: controlMrn,
    scopeMrn: space.mrn,
    types: [ExceptionType.Security],
  });

  const { handleRemoveExceptionGroups, handleSetException } = useExceptions({
    onSetException: () => {
      setSelectedChecks([]);
    },
    scopeMrns: [space.mrn],
    queryMrns: mapSelectedEntitiesToString(selectedChecks),
    refetchQueries: [
      GetComplianceControlChecksDocument,
      GetComplianceControlDocument,
    ],
  });

  const handleSetExceptionModalOpen = () => {
    dialogs.open(SetExceptionDialog, {
      target: "check",
      targetData: {
        targetTitle: `${complianceControl?.title} on ${frameworkName}`,
      },
      onSave: handleSetException,
      selectedEntities: selectedChecks,
      shouldListEntities: true,
    });
  };

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedChecks([]);
      },
      exceptionMrns: exceptionGroups.map((eg) => eg.mrn),
    });
  };

  const { complianceControl } = data || {};
  const { checks } = complianceControl || {};
  const checksEdges = checks?.edges ?? [];
  const checksArray = checksEdges.flatMap((edge) =>
    edge.node?.__typename === "QueryCheck" ? edge.node : [],
  );

  setDocumentTitle([
    "Findings",
    control.title,
    frameworkName,
    "Compliance",
    space.name,
  ]);

  if (loading && !checks) {
    return <Loading what="checks" />;
  }

  if (error) {
    return <LoadingFailed what="checks" />;
  }

  if (!checksArray.length) {
    return (
      <EmptyState
        contentType="control-checks"
        scope={space}
        frameworkMrn={frameworkMrn}
      />
    );
  }

  const targetPaginatedGroup = checksArray
    .slice(pageItems.from, pageItems.to)
    .map((check) => ({
      title: check.mquery.title,
      mrn: check.mquery.mrn as string,
      exception: check.exception,
      groupId: check?.exception?.id,
    }));

  const handlePendingReviewClick = (exception?: Maybe<{ id: string }>) => {
    if (!exception) return;
    navigate(
      `/space/compliance/${frameworkId}/controls/${controlId}/exceptions?spaceId=${space.id}&frameworkMrn=${frameworkMrn}&controlMrn=${controlMrn}&exceptionId=${exception?.id}`,
    );
  };

  const handleCheckClick = (check: QueryCheck) => {
    navigate(
      `/space/compliance/${frameworkId}/controls/${controlId}/check?spaceId=${space.id}&findingMrn=${check.mquery.mrn}&controlMrn=${controlMrn}&frameworkMrn=${frameworkMrn}`,
      { state: { control, frameworkMrn } },
    );
  };

  const tableHeaders: Header[] = [
    hasApplyExceptionPermission
      ? {
          id: "SELECT",
          label: (
            <Checkbox
              checked={isGroupChecked(targetPaginatedGroup)}
              indeterminate={isGroupIndeterminate(targetPaginatedGroup)}
              onChange={(event) =>
                onGroupCheckChange(event, targetPaginatedGroup)
              }
            />
          ),
          sortable: false,
        }
      : undefined,
    { id: "IMPACT", label: "Impact", sortable: false },
    { id: "NAME", label: "Check / Policy" },
    { id: "ASSETS_COUNT", label: "Assets" },
    { id: "COMPLETION", label: "Completion", sortable: true },
  ].flatMap((h) => h ?? []);

  return (
    <Box>
      {/* Checks does not currently have search enabled */}
      {/* <FilterBar
        searchId="compliance-checks"
        placeholder="compliance_checks"
        searchFilters={searchFilters}
        handleFilterQuery={handleFilterQuery}
      /> */}

      {loading ? (
        <ComplianceControlChecksSkeleton />
      ) : (
        <>
          <ComplianceTable
            tableHeaders={tableHeaders}
            defaultActiveColumn={ChecksOrderField.Name}
            selection={selectedChecks}
            selectable={hasApplyExceptionPermission}
          >
            {checksArray.slice(pageItems.from, pageItems.to).map((check) => {
              const { state } = check;

              const isRiskAccepted =
                check?.exception?.action ===
                ExceptionMutationAction.RiskAccepted;
              const isDisabled =
                check?.exception?.action === ExceptionMutationAction.Disable;
              const isWorkaround =
                check?.exception?.action === ExceptionMutationAction.Workaround;
              const isFalsePositive =
                check?.exception?.action ===
                ExceptionMutationAction.FalsePositive;
              const isModified = state !== CheckState.Active;
              const isPendingException =
                check.exception?.status === ExceptionStatus.New;

              return (
                <TableRow
                  key={check?.mquery?.mrn}
                  onClick={() => handleCheckClick(check)}
                  // to={buildUrl(check)}
                  sx={{
                    "& .MuiTableCell-root": { fontWeight: 700 },
                    ...(!check.completion && {
                      "& .MuiTableCell-root": {
                        color: "text.secondary",
                        fontWeight: 700,
                      },
                      "&:hover .MuiTableCell-root:before": {
                        background: "transparent",
                      },
                    }),
                  }}
                >
                  {hasApplyExceptionPermission && (
                    <TableCell>
                      <Checkbox
                        onClick={(e) => handleNodeClick(e)}
                        onChange={(e, changed) =>
                          handleNodeChange(e, changed, {
                            title: check?.mquery.title,
                            mrn: check.mquery.mrn,
                            exception: check.exception,
                          })
                        }
                        checked={Boolean(
                          selectedChecks.find(
                            (selectedCheck) =>
                              check.mquery.mrn === selectedCheck.mrn,
                          ),
                        )}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ opacity: isModified ? 0.5 : 1 }}>
                    {check.mquery?.impact?.value !== undefined && (
                      <ImpactUpdated
                        isActive
                        impact={{
                          value: check.mquery?.impact?.value,
                          rating: check.mquery.impact.rating,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ opacity: isModified ? 0.5 : 1 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {isDisabled && (
                        <Chip
                          label="Disabled"
                          icon={<DisabledIcon />}
                          size="small"
                        />
                      )}
                      {isRiskAccepted && (
                        <Chip
                          label="Risk Accepted"
                          icon={<ThumbUpOffAltIcon />}
                          size="small"
                        />
                      )}
                      {isWorkaround && (
                        <Chip
                          label="Workaround"
                          icon={<WorkaroundIcon />}
                          size="small"
                        />
                      )}
                      {isFalsePositive && (
                        <Chip
                          label="False Positive"
                          icon={<FalsePositiveIcon />}
                          size="small"
                        />
                      )}
                      {isPendingException && (
                        <NavigateToExceptionButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePendingReviewClick(check?.exception);
                          }}
                        />
                      )}
                      <Typography className="mquery-title" fontSize={14}>
                        {check?.mquery?.title}
                      </Typography>
                    </Box>
                    {DedupRefsNames(check.refs).map((ref) => (
                      <Typography
                        key={ref.mrn}
                        variant="caption"
                        color="text.secondary"
                        fontSize={10}
                      >
                        {ref.name}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell sx={{ opacity: isModified ? 0.5 : 1 }}>
                    {check?.assets || check?.assets === 0 ? check.assets : "—"}
                  </TableCell>
                  <TableCell
                    sx={{ position: "relative", opacity: isModified ? 0.5 : 1 }}
                  >
                    <CompletionCell
                      assets={check?.assets}
                      completionData={{
                        percent: check?.completion || 0,
                        height: theme.spacing(1),
                        tooltip: `Completion: ${check?.completion}%`,
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </ComplianceTable>
          <Pagination
            fetchMore={fetchMore}
            pageInfo={complianceControl?.checks?.pageInfo}
            totalCount={complianceControl?.checks?.totalCount || 0}
            setPageItems={setPageItems}
          />
        </>
      )}
    </Box>
  );
}

// we currently do not show the ref group of the check, so we ensure we only show unique ref names instead
export function DedupRefsNames(array: PolicyOrPackRef[]): PolicyOrPackRef[] {
  let unique: {
    [key: PolicyOrPackRef["mrn"]]: PolicyOrPackRef;
  } = {};
  array.forEach((item) => {
    unique[item.mrn] = item;
  });
  return Object.values(unique);
}
